/**
 * HLOS-140 — useConversationTurns (composable de turno do conversation).
 *
 * Encapsula o adapter `pyodideCaller` + handler `onSubmit` que HLOS-138
 * deixou inline no ConversationChat.vue (inflando o component p/ 220 LOC).
 *
 * **Fluxo (HLOS-38):** orchestrator (companion.processTurn) é o ÚNICO a
 * adicionar learner + companion na conversation store (single source of
 * truth). Este composable apenas delega e trata o fallback de erro.
 *
 * **Fase 1 (Lume) A3/A4/A7:** onSubmit aceita attachments (imagem/sketch).
 * Anexos são preservados em metadata do turno (futuro kernel pode processar
 * multimodal); em v0.x, anexos são listados na resposta do companion
 * para fins de audit (CL-09).
 *
 * **Constitucional:**
 *   - CC-05: fallback de erro sem penalidade ("Pode tentar de novo?")
 *   - Princípio XI: role 'companion', copy sem provedor
 *
 * **Ref:** HLOS-140, HLOS-138, HLOS-38, HLOS-15.
 */

import { useConversation } from "./useConversation";
import { useCompanion } from "../../companion/composables/useCompanion";
import { useProcessTurn } from "../../composables/useProcessTurn";
import type {
  KernelTurnResponse,
  CompanionTurnInput,
} from "../../companion/types";
import type { Attachment } from "../../multimodal/types";

export function useConversationTurns(): {
  /** Submete um turno do learner via orchestrator. */
  onSubmit: (text: string, attachments: Attachment[]) => Promise<void>;
} {
  const conv = useConversation();
  const companion = useCompanion();
  const processTurnComposable = useProcessTurn();

  /**
   * Adapter: useProcessTurn (bridge Pyodide real ou mock) → KernelTurnResponse
   * (formato que o orchestrator espera). HLOS-38.
   *
   * HLOS-230: propaga `result.initiativeText` (oferta de continuação)
   * como `offer` no `KernelTurnResponse`. Antes a oferta era descartada
   * (lida apenas para calcular `userAcceptedProvocation`). Agora chega
   * à UI e é renderizada por MessageBubble (CC-01 affordance visível).
   */
  const pyodideCaller = async (
    input: CompanionTurnInput,
  ): Promise<KernelTurnResponse> => {
    // HLOS-323: propaga anexos para o kernel Python. Vision bridge
    // será chamada pelo lado Python (local_provider_apple.py).
    const result = await processTurnComposable.processUserTurn({
      text: input.text,
      ...(input.attachments && input.attachments.length > 0
        ? {
            attachments: input.attachments.map((a) => ({
              kind: a.kind,
              dataUrl: a.dataUrl,
              filename: a.filename,
            })),
          }
        : {}),
    });
    return {
      response: result.reply,
      // HLOS-230: propaga offer (CC-01). Antes era lido apenas para
      // `userAcceptedProvocation`; agora persiste no Message.
      offer: result.initiativeText,
      curiosityGaps: result.curiosityGaps.map((text, i) => ({
        id: `gap_${Date.now()}_${i}`,
        domain: text,
        kaDepth: 0.5,
      })),
      focusItems: [],
      userAcceptedProvocation:
        result.rLevel === "R2" && result.initiativeText !== null,
    };
  };

  async function onSubmit(
    text: string,
    attachments: Attachment[],
  ): Promise<void> {
    // HLOS-319: anexos ainda não são processados pelo kernel (v0.x).
    // Por enquanto, anexos são listados no log do turno para audit (CL-09).
    // Quando o kernel ganhar capacidade multimodal, esta chamada é atualizada.
    if (attachments.length > 0) {
      // eslint-disable-next-line no-console
      console.info(
        "[turn] attachments received:",
        attachments.map((a) => ({ kind: a.kind, filename: a.filename })),
      );
    }
    // HLOS-348: Vision está implementado (HLOS-323). O kernel recebe
    // o vision_context (OCR + tags extraídos on-device via Apple Vision)
    // e responde com base no que VÊ. Não precisamos do disclosure
    // "AVISO: você NÃO consegue ver" — o LLM agora VÊ o conteúdo.
    //
    // Mantemos o prefixo curto "Anexo: X" só para contexto.
    let textForKernel = text;
    if (attachments.length > 0) {
      const attSummary = attachments
        .map((a) => `${a.kind}${a.filename ? ` (${a.filename})` : ""}`)
        .join(", ");
      textForKernel = `[Anexo: ${attSummary}] ` + text;
    }
    const result = await companion.processTurn(
      {
        text: textForKernel,
        clientTimestampMs: Date.now(),
        // HLOS-319: propaga anexos para o orchestrator. Serão renderizados
        // inline na mensagem do learner (MessageBubble). Filtra para
        // apenas image/sketch (MessageAttachment não inclui 'audio' ainda).
        attachments: attachments
          .filter((a) => a.kind === "image" || a.kind === "sketch")
          .map((a) => ({
            kind: a.kind as "image" | "sketch",
            dataUrl: a.dataUrl,
            filename: a.filename,
          })),
      },
      { pyodideCaller },
    );
    if ("error" in result) {
      conv.append(
        "companion",
        "Não consegui processar agora. Pode tentar de novo?",
      );
    }
  }

  return { onSubmit };
}
